import { useEffect, useMemo, useState } from 'react';
import { HELP_ARTICLES } from '@/content/help/_index';
import { helpSlugsForPath } from '@/lib/helpPageMap';

const HELP_SEEN_PREFIX = 'help.seen.';

function readSeen(slug: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(HELP_SEEN_PREFIX + slug) === 'true';
  } catch {
    return false;
  }
}

// useHelpUnreadForPath returns true when the current pathname has at least
// one mapped help article whose `help.seen.{slug}` flag is not yet true. The
// hook subscribes to localStorage changes so the unread dot disappears the
// moment the user opens any mapped article.
export function useHelpUnreadForPath(pathname: string): boolean {
  const knownSlugs = useMemo(
    () => new Set(HELP_ARTICLES.map((a) => a.slug)),
    [],
  );
  const slugs = useMemo(
    () => helpSlugsForPath(pathname, knownSlugs),
    [pathname, knownSlugs],
  );
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (!e.key || !e.key.startsWith(HELP_SEEN_PREFIX)) return;
      setTick((t) => t + 1);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
  return useMemo(() => {
    // Re-evaluate when tick increments so the hook responds to in-tab writes.
    void tick;
    return slugs.some((slug) => !readSeen(slug));
  }, [slugs, tick]);
}

export function markHelpSeen(slug: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HELP_SEEN_PREFIX + slug, 'true');
  } catch {
    // ignored
  }
}
